import type { Billing, Subscription } from '@stores/Billing';
import type { User } from '@stores/User';

type SubscriptionStatus = NonNullable<Subscription>['status'];

/**
 * How to describe an attached subscription's status to its owner, as the tail of "Your plan
 * is …". Every status is listed so a new one from Stripe fails the build here rather than
 * rendering an empty sentence.
 */
const SUBSCRIPTION_STATUS_DESCRIPTIONS: Record<SubscriptionStatus, string> = {
  active: 'active',
  trialing: 'in its trial period',
  past_due: "waiting on a payment that didn't go through",
  unpaid: "unpaid — the last payment didn't go through",
  paused: 'paused',
  incomplete: 'waiting for its first payment',
  incomplete_expired: 'no longer active',
  canceled: 'no longer active',
};

export const describeSubscriptionStatus = (status: SubscriptionStatus): string =>
  SUBSCRIPTION_STATUS_DESCRIPTIONS[status];

/**
 * What the user's own billing account needs before the app opens up. One value, read by
 * the gate in `WithUser`, the wall, Settings → Billing and the Pricing CTAs, so every
 * surface answers "what should this user do next" the same way:
 *
 * - `entitled`  — a paying plan; nothing to do.
 * - `past-due`  — still entitled, but the last renewal failed and Stripe is retrying; the
 *                 user should fix their card in the portal.
 * - `no-plan`   — nothing attached; the user picks a plan.
 * - `lapsed`    — a subscription is attached but no longer entitles (`unpaid`, `paused`,
 *                 `incomplete`). Buying a new plan is refused by the backend while one is
 *                 attached, so the only way forward is the Stripe portal.
 *
 * The verdict comes from the backend's `isFree` flag, never from `subscription != null`: a
 * lapsed subscription is attached and still reads free.
 */
export type PlanAccess = 'entitled' | 'past-due' | 'no-plan' | 'lapsed';

export const isWalled = (access: PlanAccess | null): boolean =>
  access === 'no-plan' || access === 'lapsed';

type BillingLike = Pick<Billing, 'isFree' | 'isPastDue' | 'subscription'>;
type UserLike = Pick<User, 'isFree' | 'isPastDue'>;

export function planAccessFromBilling(billing: BillingLike): PlanAccess {
  // `!isFree` also covers a backend that predates the flag: an account it can't describe
  // is let in rather than walled, and the backend's own 402s still stop paid actions.
  if (!billing.isFree) return billing.isPastDue ? 'past-due' : 'entitled';
  return billing.subscription ? 'lapsed' : 'no-plan';
}

/**
 * Resolve the verdict from whatever loaded. `billing` is null only when the fetch failed
 * (`WithUser` waits for it otherwise). Then `/user/me`'s copy of the flags is the fallback,
 * and only a positive "subscribed" is trusted from it: that copy is cached for ~30s and
 * carries no `subscription`, so a free reading can't say which wall to show. That case
 * returns null — undecided — and the caller shows a retry, not a guess.
 */
export function resolvePlanAccess(
  billing: BillingLike | null,
  user: UserLike | null | undefined,
): PlanAccess | null {
  if (billing) return planAccessFromBilling(billing);
  if (user?.isFree === false) return user.isPastDue ? 'past-due' : 'entitled';
  return null;
}
